import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, CalendarCheck, History, X, GraduationCap, Award, Flag, FolderOpen, Search } from "lucide-react";
import { ClientPipeline, STAGE_SHORT, STAGE_COUNT } from "../components/rm/ClientPipeline";
import { DiscoveryFormDialog } from "../components/rm/DiscoveryFormDialog";
import { DiscoverySummary } from "../components/rm/DiscoverySummary";
import { ClientProfilePanel } from "../components/rm/ClientProfilePanel";
import { AccountStatusToggle } from "../components/rm/AccountStatusToggle";

export const STATUS_OPTIONS = [
  { value: "active", label: "Active", badge: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "paused", label: "Paused", badge: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "non_responsive", label: "Non-Responsive", badge: "bg-red-100 text-red-700 border-red-300" },
  { value: "not_ready", label: "Not Ready to Capital Raise", badge: "bg-slate-200 text-slate-700 border-slate-300" },
  { value: "other", label: "Other", badge: "bg-sky-100 text-sky-800 border-sky-300" },
];

export const SERVICES = [
  { key: "financial_analyst_support", label: "Financial Analyst Support" },
  { key: "masterclass", label: "Masterclass" },
  { key: "training_cohort", label: "Training Cohort" },
  { key: "monthly_webinar", label: "Monthly Webinar" },
];

const STEP_LABELS = { 1: "Get my report", 2: "Tell us about your business", 3: "Upload your documents", 4: "We score your business", 5: "Your Score and Report" };

const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");
const todayStr = () => new Date().toISOString().slice(0, 10);
const isOverdue = (due) => due && due < todayStr();

export const TIERS = {
  bronze: { label: "Bronze", badge: "border-amber-700/30 bg-amber-50 text-amber-800" },
  silver: { label: "Silver", badge: "border-slate-300 bg-slate-100 text-slate-700" },
  gold: { label: "Gold", badge: "border-yellow-400 bg-yellow-50 text-yellow-800" },
  platinum: { label: "Platinum", badge: "border-[#0A8B85] bg-[#0A8B85]/10 text-[#0A8B85]" },
};
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

function TierBadge({ r }) {
  const t = TIERS[r.tier];
  const since = daysSince(r.last_checkin_at);
  const urgent = r.tier === "platinum" && (since === null || since >= 7);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${
        t ? t.badge : "border-slate-200 bg-slate-50 text-slate-400"}`}
        title="Client tier by amount raising: < KES 50M Bronze · 50–100M Silver · 100–500M Gold · 500M+ Platinum"
        data-testid={`rm-tier-badge-${r.company_id}`}>
        {t ? t.label : "Unclassified"}
      </span>
      {urgent && (
        <span className="inline-block rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700"
          data-testid={`rm-tier-urgent-${r.company_id}`}>
          {since === null ? "No contact yet — urgent" : `${since}d since contact — urgent`}
        </span>
      )}
    </span>
  );
}

function Modal({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

function CheckinDialog({ client, onClose, onDone }) {
  const [date, setDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [actionItems, setActionItems] = useState("");
  const [assignedTo, setAssignedTo] = useState("rm");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rm/clients/${client.company_id}/checkin`, {
        date, note, action_items: actionItems,
        action_items_assigned_to: actionItems.trim() ? assignedTo : null,
      });
      toast.success("Check-in logged");
      onDone(client.company_id, data);
      onClose();
    } catch (_) { toast.error("Couldn't log the check-in — try again"); }
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} testId="rm-checkin-dialog">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900">Log check-in</h3>
          <p className="mt-0.5 text-xs text-slate-500">{client.client_name} · {client.company_name}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="rm-checkin-close"><X size={18} /></button>
      </div>
      <label className="mt-4 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Date of check-in</label>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
        data-testid="rm-checkin-date" />
      <label className="mt-3 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Notes from the interaction</label>
      <textarea autoFocus rows={3} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="What was discussed?"
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
        data-testid="rm-checkin-note" />
      <label className="mt-3 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Action items</label>
      <textarea rows={2} value={actionItems} onChange={(e) => setActionItems(e.target.value)}
        placeholder="What needs to happen next? (optional)"
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
        data-testid="rm-checkin-actions" />
      {actionItems.trim() && (
        <div className="mt-3">
          <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500">Action items assigned to</label>
          <div className="mt-1 flex gap-2">
            {[["rm", "RM"], ["client", "Client"]].map(([v, l]) => (
              <button key={v} onClick={() => setAssignedTo(v)}
                className={`rounded-full border px-4 py-1.5 text-xs font-bold ${assignedTo === v
                  ? "border-[#0A8B85] bg-[#0A8B85]/10 text-[#0A8B85]" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                data-testid={`rm-checkin-assigned-${v}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-400">Next check-in will auto-set to 14 days after this date.</p>
      <button onClick={submit} disabled={busy}
        className="mt-4 w-full rounded-lg bg-[#0A8B85] py-2.5 text-sm font-bold text-white hover:bg-[#097a74] disabled:opacity-50"
        data-testid="rm-checkin-submit">
        {busy ? "Saving…" : "Log check-in"}
      </button>
    </Modal>
  );
}

function ReferralFlagDialog({ client, onClose, onFlagged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!note.trim()) { toast.error("Please describe the founder's need first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/rm/clients/${client.company_id}/referral-flag`, { note: note.trim() });
      toast.success("Referral flagged — Doreen has been notified");
      onFlagged(client.company_id, data.referral_flag);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't log the flag — try again"); }
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} testId="rm-referral-dialog">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900">Outsourced partner referral</h3>
          <p className="mt-0.5 text-xs text-slate-500">{client.client_name} · {client.company_name}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="rm-referral-close"><X size={18} /></button>
      </div>
      <label className="mt-4 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Founder's need (required)</label>
      <textarea autoFocus rows={3} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder='e.g. "Wants full investor-readiness support — modelling, deck and investor intros"'
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
        data-testid="rm-referral-note" />
      <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
        Module 9 protocol: this logs the flag and notifies Doreen instantly. She owns the
        partner conversation, fees and handover — don't quote fees or make commitments.
      </p>
      <button onClick={submit} disabled={busy || !note.trim()}
        className="mt-4 w-full rounded-lg bg-[#0A8B85] py-2.5 text-sm font-bold text-white hover:bg-[#097a74] disabled:opacity-50"
        data-testid="rm-referral-submit">
        {busy ? "Flagging…" : "Flag referral & notify Doreen"}
      </button>
    </Modal>
  );
}

function StatusReasonDialog({ client, status, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const opt = STATUS_OPTIONS.find((o) => o.value === status);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason before saving"); return; }
    setBusy(true);
    try {
      await api.put(`/rm/clients/${client.company_id}`, { client_status: status, status_reason: reason.trim() });
      toast.success("Status updated");
      onSaved(client.company_id, { client_status: status, status_reason: reason.trim() });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save — try again"); }
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} testId="rm-status-reason-dialog">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900">Set status: {opt?.label}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{client.client_name} · {client.company_name}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="rm-status-reason-close"><X size={18} /></button>
      </div>
      <label className="mt-4 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Reason (required)</label>
      <textarea autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Why is this client moving to this status?"
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
        data-testid="rm-status-reason-input" />
      <button onClick={submit} disabled={busy || !reason.trim()}
        className="mt-4 w-full rounded-lg bg-[#0A8B85] py-2.5 text-sm font-bold text-white hover:bg-[#097a74] disabled:opacity-50"
        data-testid="rm-status-reason-save">
        {busy ? "Saving…" : "Save status"}
      </button>
    </Modal>
  );
}

function HistoryPanel({ companyId, refreshKey }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/rm/clients/${companyId}/checkins`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [companyId, refreshKey]);
  if (items === null) return <p className="py-2 text-xs text-slate-400">Loading history…</p>;
  if (items.length === 0) return <p className="py-2 text-xs text-slate-400" data-testid={`rm-history-empty-${companyId}`}>No check-ins logged yet.</p>;
  return (
    <div className="mt-2 space-y-2" data-testid={`rm-history-${companyId}`}>
      {items.map((c) => (
        <div key={c.checkin_id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <p className="text-[11px] font-semibold text-slate-500">{c.checkin_date || fmtDate(c.created_at)} · {c.rm_name}</p>
          {c.note && <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-700">{c.note}</p>}
          {c.action_items && (
            <p className="mt-1 text-xs text-slate-700">
              <span className={`mr-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                c.action_items_assigned_to === "client" ? "bg-teal-100 text-teal-700" : "bg-sky-100 text-sky-700"}`}>
                {c.action_items_assigned_to === "client" ? "Client" : "RM"}
              </span>
              <span className="font-semibold text-slate-500">Action items:</span> {c.action_items}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function ClientCard({ r, onSave, onCheckin, onServiceToggle, historyOpen, toggleHistory, onStatusPick, onReferral, onAccountChanged, onDiscovery, onRefresh }) {
  const [notes, setNotes] = useState(r.notes || "");
  const [profileOpen, setProfileOpen] = useState(false);
  const id = r.company_id;
  const overdue = isOverdue(r.next_checkin_due);
  const statusOpt = STATUS_OPTIONS.find((o) => o.value === r.client_status);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`rm-client-card-${id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-extrabold text-slate-900">{r.client_name}</p>
          <p className="text-sm font-semibold text-[#0A8B85]">{r.company_name}</p>
          <p className="text-[11px] text-slate-400">{r.client_email}</p>
          <div className="mt-1.5"><TierBadge r={r} /></div>
        </div>
        <div className="text-right">
          <span className={`inline-block rounded-full border px-3 py-1 text-xs font-extrabold uppercase tracking-wide ${
            statusOpt ? statusOpt.badge : "border-slate-200 bg-slate-50 text-slate-400"}`}
            data-testid={`rm-status-badge-${id}`}>
            {statusOpt ? statusOpt.label : "No status"}
          </span>
          {r.status_reason && r.client_status !== "active" && (
            <p className="mt-1 max-w-[240px] text-[11px] italic text-slate-500" data-testid={`rm-status-reason-${id}`}>
              {r.status_reason}
            </p>
          )}
          <select value={r.client_status || ""}
            onChange={(e) => onStatusPick(r, e.target.value)}
            className="mt-1.5 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 outline-none focus:border-[#0A8B85]"
            data-testid={`rm-status-${id}`}>
            <option value="">— Set status —</option>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Account</span>
        <AccountStatusToggle r={r} onChanged={onAccountChanged} />
      </div>

      <ClientPipeline r={r} onSave={onSave} onDiscovery={() => onDiscovery(r)} />
      <DiscoverySummary discovery={r.discovery} testid={`rm-discovery-summary-${id}`} />

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <span className="rounded-lg bg-slate-50 px-2.5 py-1.5 font-semibold text-slate-600" title={STEP_LABELS[r.stage]}>Onboarding step <b className="text-[#0A8B85]" data-testid={`rm-stage-${id}`}>{r.stage}</b>/5 · <span className="font-normal">{STEP_LABELS[r.stage]}</span></span>
        <span className="rounded-lg bg-slate-50 px-2.5 py-1.5 font-semibold text-slate-600">CRS <b className="text-[#0A8B85]" data-testid={`rm-crs-${id}`}>{r.crs_score ?? "—"}</b></span>
        <span className="rounded-lg bg-slate-50 px-2.5 py-1.5 font-semibold text-slate-600" data-testid={`rm-report-${id}`}>
          Report {(r.report_downloaded || r.stage === 5) ? <b className="text-emerald-600">Yes</b> : <b className="text-slate-400">No</b>}
        </span>
        <span className="rounded-lg bg-slate-50 px-2.5 py-1.5 font-semibold text-slate-600" data-testid={`rm-last-checkin-${id}`}>
          Last check-in <b className="text-slate-800">{fmtDate(r.last_checkin_at)}</b>
        </span>
        <span className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-semibold ${overdue ? "bg-red-50 text-red-600" : "bg-slate-50 text-slate-600"}`}>
          Next due
          <input type="date" value={r.next_checkin_due || ""}
            onChange={(e) => onSave(id, { next_checkin_override: e.target.value }, { next_checkin_due: e.target.value })}
            className={`rounded border bg-white px-1.5 py-0.5 text-[11px] outline-none focus:border-[#0A8B85] ${overdue ? "border-red-300 font-bold text-red-700" : "border-slate-200 text-slate-700"}`}
            data-testid={`rm-next-due-${id}`} />
          {overdue && <b className="text-[10px] uppercase">Overdue</b>}
        </span>
      </div>

      <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
        onBlur={() => { if (notes !== (r.notes || "")) onSave(id, { notes }, { notes }); }}
        placeholder="General notes on this client…"
        className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 outline-none focus:border-[#0A8B85]"
        data-testid={`rm-notes-${id}`} />

      <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5" data-testid={`rm-services-${id}`}>
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Client has expressed interest in the following services</p>
        <p className="mt-0.5 text-[10px] text-slate-400">Tick each service the client has said they are interested in.</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {SERVICES.map((s) => {
            const rec = (r.services_offered || {})[s.key];
            return (
              <label key={s.key} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                rec ? "border-[#0A8B85] bg-[#0A8B85]/10 text-[#0A8B85]" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                <input type="checkbox" checked={!!rec}
                  onChange={(e) => onServiceToggle(id, s.key, e.target.checked)}
                  className="h-3 w-3 accent-[#0A8B85]"
                  data-testid={`rm-service-${s.key}-${id}`} />
                {s.label}
                {rec && <span className="text-[10px] font-normal text-slate-400">interested since {rec.introduced_at}</span>}
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button onClick={() => onCheckin(r)}
          className="flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#097a74]"
          data-testid={`rm-log-checkin-${id}`}>
          <CalendarCheck size={14} /> Log check-in
        </button>
        <button onClick={() => toggleHistory(id)}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          data-testid={`rm-history-toggle-${id}`}>
          <History size={14} /> Check-in log ({r.checkin_count})
        </button>
        <button onClick={() => setProfileOpen((o) => !o)}
          className={`flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-xs font-semibold ${profileOpen ? "border-[#0A8B85] bg-[#0A8B85]/10 text-[#0A8B85]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid={`rm-profile-toggle-${id}`}>
          <FolderOpen size={14} /> Profile &amp; documents
        </button>
        {r.referral_flag ? (
          <span className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-bold text-amber-700"
            title={r.referral_flag.note}
            data-testid={`rm-referral-badge-${id}`}>
            <Flag size={14} /> Referral flagged — with Doreen · {fmtDate(r.referral_flag.flagged_at)}
          </span>
        ) : (
          <button onClick={() => onReferral(r)}
            className="flex items-center gap-1.5 rounded-lg border border-amber-300 px-3.5 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-50"
            data-testid={`rm-referral-flag-${id}`}>
            <Flag size={14} /> Outsourced partner referral
          </button>
        )}
      </div>
      {profileOpen && <ClientProfilePanel companyId={id} clientName={r.client_name} client={r} onRefresh={onRefresh} />}
      {historyOpen && <HistoryPanel companyId={id} refreshKey={r.checkin_count} />}
    </div>
  );
}

export default function RMDashboard() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkinFor, setCheckinFor] = useState(null);
  const [statusPick, setStatusPick] = useState(null);
  const [referralFor, setReferralFor] = useState(null);
  const [discoveryFor, setDiscoveryFor] = useState(null);
  const [openHistory, setOpenHistory] = useState({});
  const [certification, setCertification] = useState(null);
  const [accountFilter, setAccountFilter] = useState("all"); // all | active | dormant
  const [stageFilter, setStageFilter] = useState("all"); // all | 0..5
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/rm/training")
      .then((r) => setCertification(r.data.certification || null))
      .catch(() => {});
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/rm/clients");
      setRows(data.rows || []);
    } catch (_) { toast.error("Couldn't load your clients"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const patchRow = (companyId, patch) =>
    setRows((rs) => rs.map((r) => (r.company_id === companyId ? { ...r, ...patch } : r)));

  const save = async (companyId, fields, optimistic) => {
    patchRow(companyId, optimistic);
    try {
      await api.put(`/rm/clients/${companyId}`, fields);
    } catch (_) { toast.error("Couldn't save — try again"); }
  };

  const onStatusPick = (r, value) => {
    if (!value || value === r.client_status) return;
    if (value === "active") {
      save(r.company_id, { client_status: "active" }, { client_status: "active", status_reason: null });
    } else {
      setStatusPick({ client: r, status: value });
    }
  };

  const onServiceToggle = async (companyId, service, offered) => {
    try {
      const { data } = await api.put(`/rm/clients/${companyId}/services`, { service, offered });
      patchRow(companyId, { services_offered: data.services_offered });
    } catch (_) { toast.error("Couldn't update services — try again"); }
  };

  const onCheckinDone = (companyId, data) => {
    setRows((rs) => rs.map((r) => (r.company_id === companyId
      ? { ...r, last_checkin_at: data.last_checkin_at, next_checkin_due: data.next_checkin_due, checkin_count: (r.checkin_count || 0) + 1 }
      : r)));
  };

  const dueSoon = rows.filter((r) => r.next_checkin_due && r.next_checkin_due >= todayStr()
    && r.next_checkin_due <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10)).length;
  const overdue = rows.filter((r) => isOverdue(r.next_checkin_due)).length;
  const dormantCount = rows.filter((r) => r.account_status === "dormant").length;
  const byAccount = accountFilter === "all" ? rows : rows.filter((r) => (r.account_status || "active") === accountFilter);
  const byStage = stageFilter === "all" ? byAccount : byAccount.filter((r) => (r.pipeline?.current_stage ?? 0) === Number(stageFilter));
  const q = search.trim().toLowerCase();
  const matchesSearch = (r) => !q || [r.client_name, r.client_email, r.company_name].some((v) => (v || "").toLowerCase().includes(q));
  // matches float to the top; the rest stay below in their usual order
  const visibleRows = q ? [...byStage.filter(matchesSearch), ...byStage.filter((r) => !matchesSearch(r))] : byStage;
  const searchHits = q ? byStage.filter(matchesSearch).length : null;
  const stageCounts = Array.from({ length: STAGE_COUNT + 1 }, (_, n) => n).map((n) => byAccount.filter((r) => (r.pipeline?.current_stage ?? 0) === n).length);

  return (
    <div className="min-h-screen bg-[#F4F8F8]" data-testid="rm-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <p className="text-lg font-extrabold tracking-tight text-[#0A2540]">Clinq <span className="text-[#0A8B85]">RM Portal</span></p>
            <p className="text-xs text-slate-500">Relationship Manager · {user?.name}</p>
            {certification && (
              <p className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]" data-testid="rm-certification-badge">
                <Award size={11} /> Certified Clinq RM · {certification.certified_at?.slice(0, 10)} · {certification.score_pct}%
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a href="/rm/training"
              className="flex items-center gap-1.5 rounded-lg border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-3 py-1.5 text-xs font-semibold text-[#0A8B85] hover:bg-[#0A8B85]/10"
              data-testid="rm-nav-training-link">
              <GraduationCap size={14} /> RM Training
            </a>
            <button onClick={async () => { await logout(); window.location.assign("/login"); }}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="rm-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-2xl font-extrabold text-[#0A2540]" data-testid="rm-title">My Clients</h1>
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Assigned clients</p>
            <p className="text-xl font-extrabold text-[#0A2540]" data-testid="rm-stat-clients">{rows.length}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Due this week</p>
            <p className="text-xl font-extrabold text-amber-600" data-testid="rm-stat-due">{dueSoon}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Overdue</p>
            <p className="text-xl font-extrabold text-red-600" data-testid="rm-stat-overdue">{overdue}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Dormant</p>
            <p className="text-xl font-extrabold text-slate-600" data-testid="rm-stat-dormant">{dormantCount}</p>
          </div>
        </div>

        <div className="relative mt-5" data-testid="rm-search-wrap">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search founders by name or email…" aria-label="Search founders by name or email"
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#0A8B85] focus:outline-none focus:ring-2 focus:ring-[#0A8B85]/20"
            data-testid="rm-search-input" />
          {search && (
            <button type="button" onClick={() => setSearch("")} aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:text-slate-700" data-testid="rm-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
          {searchHits !== null && (
            <p className="mt-1.5 text-xs text-slate-500" data-testid="rm-search-hits">
              {searchHits === 0 ? `No founders match "${search.trim()}" — showing everyone below.` : `${searchHits} match${searchHits === 1 ? "" : "es"} shown first.`}
            </p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="rm-account-filter">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Show</span>
          {[["all", `All (${rows.length})`], ["active", `Active (${rows.length - dormantCount})`], ["dormant", `Dormant (${dormantCount})`]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setAccountFilter(v)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${accountFilter === v
                ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#0A8B85]"}`}
              data-testid={`rm-account-filter-${v}`}>{label}</button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="rm-stage-filter">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Pipeline stage</span>
          <button type="button" onClick={() => setStageFilter("all")}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${stageFilter === "all" ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#0A2540]"}`}
            data-testid="rm-stage-filter-all">All stages</button>
          {Array.from({ length: STAGE_COUNT + 1 }, (_, n) => n).map((n) => (
            <button key={n} type="button" onClick={() => setStageFilter(String(n))}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${stageFilter === String(n) ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#0A2540]"}`}
              data-testid={`rm-stage-filter-${n}`}>{n === 0 ? "Not started" : `Stage ${n} · ${STAGE_SHORT[n]}`} ({stageCounts[n]})</button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <p className="py-12 text-center text-slate-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-slate-400" data-testid="rm-empty-state">
              No clients assigned to you yet. Your admin will assign clients from the admin panel.
            </p>
          ) : visibleRows.length === 0 ? (
            <p className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-slate-400" data-testid="rm-filter-empty-state">
              No clients match this filter.
            </p>
          ) : visibleRows.map((r) => (
            <ClientCard key={r.company_id} r={r} onSave={save}
              onRefresh={load}
              onAccountChanged={patchRow}
              onCheckin={setCheckinFor}
              onReferral={setReferralFor}
              onDiscovery={setDiscoveryFor}
              onStatusPick={onStatusPick}
              onServiceToggle={onServiceToggle}
              historyOpen={!!openHistory[r.company_id]}
              toggleHistory={(id) => setOpenHistory((h) => ({ ...h, [id]: !h[id] }))} />
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Notes and next-due changes save automatically. Setting a non-Active status requires a reason and alerts the Clinq team.</p>
      </main>

      {checkinFor && (
        <CheckinDialog client={checkinFor} onClose={() => setCheckinFor(null)} onDone={onCheckinDone} />
      )}
      {statusPick && (
        <StatusReasonDialog client={statusPick.client} status={statusPick.status}
          onClose={() => setStatusPick(null)}
          onSaved={(companyId, patch) => patchRow(companyId, patch)} />
      )}
      {discoveryFor && (
        <DiscoveryFormDialog client={discoveryFor} onClose={() => setDiscoveryFor(null)}
          onSubmitted={(companyId, submittedAt) => {
            const row = rows.find((x) => x.company_id === companyId);
            if (!row?.pipeline) return;
            const stages = row.pipeline.stages.map((s) => (s.n === 2 ? { ...s, done: true, date: (submittedAt || "").slice(0, 10) } : s));
            const done = stages.filter((s) => s.done).map((s) => s.n);
            const current = Math.max(...done);
            patchRow(companyId, { pipeline: { ...row.pipeline, stages, completed: done.length, current_stage: current, current_label: stages.find((s) => s.n === current).label } });
            api.get(`/rm/clients/${companyId}/discovery`).then((res) => patchRow(companyId, { discovery: res.data.form ? { form: res.data.form, submitted_at: res.data.submitted_at } : null })).catch(() => {});
          }} />
      )}
      {referralFor && (
        <ReferralFlagDialog client={referralFor} onClose={() => setReferralFor(null)}
          onFlagged={(companyId, flag) => patchRow(companyId, { referral_flag: flag })} />
      )}
    </div>
  );
}
